/**
 * SynthesisSkillEffect — Poison tree Expel path apotheosis.
 *
 * A 2-second channel that converts all active poison within 3 tiles into
 * healing. Every poisoned enemy contributes 3% of player max HP. Every
 * poison zone contributes 1% of player max HP per second of remaining
 * duration. On resolution, all consumed poison and zones are removed —
 * ripped clean from enemy bodies and drawn out of the air.
 *
 * The patient inverse of Entropy: where Entropy cashes the DoT budget as
 * burst damage, Synthesis reclaims it as health. Every toxin the player
 * has been seeding — on enemies, in clouds, on the ground — flows back.
 * The poison was never the point. The poison was the currency.
 *
 * Channel interruption (player movement beyond a ~2px deadzone) cancels
 * the effect entirely — no partial heal, no cooldown spent. The
 * integration layer may also call `interrupt()` when the player takes
 * damage or is CC'd.
 *
 * Cooldown tier: very_long (4000ms).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Synthesis (synthesis)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TILE_SIZE = 32;

/**
 * All zone types that Synthesis considers "poison" for conversion.
 * Covers every zone the Poison tree can create — clouds, rain, hazes,
 * carrier remnants, footprints, and blight terrain.
 */
const POISON_ZONE_TYPES = new Set([
  'poison_cloud',    // Corrode
  'miasma',          // Miasma (evolved Corrode)
  'haze',            // Haze
  'acid_rain_pool',  // Acid Rain
  'carrier_cloud',   // Carrier / Charnel Ground / Virulent Remains
  'toxic_footprint', // Acid Rain Slick
  'blight',          // Blight (apotheosis)
]);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal enemy representation for targeting. */
export interface SynthesisTarget {
  readonly entityId: string;
}

/** Zone data shape matching ZoneSystem.getAll() → ActiveZone. */
export interface SynthesisZoneData {
  readonly config: {
    readonly id: string;
    readonly type: string;
    readonly position: { x: number; y: number };
  };
  readonly remainingMs: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SynthesisDeps {
  readonly targeting: {
    getPlayerPosition(): { x: number; y: number };
    getEnemiesInRange(
      center: { x: number; y: number },
      rangeTiles: number,
    ): ReadonlyArray<SynthesisTarget>;
  };
  readonly statusEffects: {
    hasPoisonStatus(entityId: string): boolean;
    removePoisonStatus(entityId: string): void;
  };
  readonly zones: {
    getAll(): ReadonlyArray<SynthesisZoneData>;
    remove(zoneId: string): void;
  };
  readonly player: {
    readonly maxHp: number;
    readonly currentHp: number;
    heal(amount: number): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SynthesisConfig {
  /** Cooldown in ms (very_long tier = 4000). */
  readonly cooldownMs: number;
  /** Channel duration in ms. */
  readonly channelDurationMs: number;
  /** Conversion range in tiles. */
  readonly rangeTiles: number;
  /** HP fraction healed per poisoned enemy (0.03 = 3%). */
  readonly healPerEnemyFraction: number;
  /** HP fraction healed per second of zone duration remaining (0.01 = 1%). */
  readonly healPerZoneSecondFraction: number;
  /** Movement threshold in pixels — beyond this, channel is interrupted. */
  readonly movementThresholdPx: number;
}

const DEFAULT_CONFIG: SynthesisConfig = {
  cooldownMs: 4000,                   // very_long tier
  channelDurationMs: 2000,            // 2-second channel
  rangeTiles: 3,
  healPerEnemyFraction: 0.03,         // 3% max HP per poisoned enemy
  healPerZoneSecondFraction: 0.01,    // 1% max HP per second of zone remaining
  movementThresholdPx: 2,             // deadzone for channel drift
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

type SynthesisState = 'idle' | 'channeling' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SynthesisSkillEffect implements SkillEffect {
  readonly skillId = 'synthesis';
  readonly type = 'active' as const;
  readonly activationMode = 'channel' as const;

  private readonly deps: SynthesisDeps;
  private readonly config: SynthesisConfig;

  private _state: SynthesisState = 'idle';
  private _channelRemainingMs = 0;
  private _cooldownRemaining = 0;
  private _channelStartPosition: { x: number; y: number } | null = null;
  private _activationsCount = 0;
  private _lastHealTotal = 0;
  private _lastEnemiesConsumed = 0;
  private _lastZonesConsumed = 0;

  constructor(deps: SynthesisDeps, config: Partial<SynthesisConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get state(): SynthesisState { return this._state; }
  get cooldown(): number { return this._state === 'cooldown' ? this._cooldownRemaining : 0; }
  get channelRemainingMs(): number { return this._channelRemainingMs; }
  get activationsCount(): number { return this._activationsCount; }
  get lastHealTotal(): number { return this._lastHealTotal; }
  get lastEnemiesConsumed(): number { return this._lastEnemiesConsumed; }
  get lastZonesConsumed(): number { return this._lastZonesConsumed; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._state === 'idle';
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._state = 'channeling';
    this._channelRemainingMs = this.config.channelDurationMs;
    this._channelStartPosition = { ...this.deps.targeting.getPlayerPosition() };
    return true;
  }

  deactivate(): void {
    this.interrupt();
  }

  isActive(): boolean {
    return this._state === 'channeling';
  }

  /**
   * Interrupt the channel externally (player hit, CC applied, etc.).
   * No heal fires. No cooldown is spent.
   */
  interrupt(): void {
    if (this._state === 'channeling') {
      this._state = 'idle';
      this._channelRemainingMs = 0;
      this._channelStartPosition = null;
    }
  }

  update(delta: number): void {
    switch (this._state) {
      case 'channeling':
        this.updateChannel(delta);
        break;
      case 'cooldown':
        this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
        if (this._cooldownRemaining <= 0) {
          this._state = 'idle';
        }
        break;
    }
  }

  destroy(): void {
    this._state = 'idle';
    this._cooldownRemaining = 0;
    this._channelRemainingMs = 0;
    this._channelStartPosition = null;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /** Tick the channel timer. Interrupts on player movement. */
  private updateChannel(delta: number): void {
    // Movement check — if the player drifts beyond the deadzone, cancel.
    if (this._channelStartPosition) {
      const pos = this.deps.targeting.getPlayerPosition();
      const dx = pos.x - this._channelStartPosition.x;
      const dy = pos.y - this._channelStartPosition.y;
      const distSq = dx * dx + dy * dy;
      const threshSq = this.config.movementThresholdPx * this.config.movementThresholdPx;
      if (distSq > threshSq) {
        this.interrupt();
        return;
      }
    }

    this._channelRemainingMs -= delta;
    if (this._channelRemainingMs <= 0) {
      this.resolveChannel();
    }
  }

  /**
   * Channel complete — convert all nearby poison into healing.
   *
   * Phase 1: Poisoned enemies within range → 3% max HP per target.
   * Phase 2: Poison zones within range → 1% max HP per second remaining.
   * Total capped at (maxHp − currentHp) to prevent overheal.
   */
  private resolveChannel(): void {
    const playerPos = this.deps.targeting.getPlayerPosition();
    const maxHp = this.deps.player.maxHp;
    let healTotal = 0;
    let enemiesConsumed = 0;
    let zonesConsumed = 0;

    // Phase 1: Poisoned enemies within range.
    const enemies = this.deps.targeting.getEnemiesInRange(
      playerPos,
      this.config.rangeTiles,
    );

    for (const enemy of enemies) {
      if (!this.deps.statusEffects.hasPoisonStatus(enemy.entityId)) continue;

      healTotal += maxHp * this.config.healPerEnemyFraction;
      this.deps.statusEffects.removePoisonStatus(enemy.entityId);
      enemiesConsumed++;
    }

    // Phase 2: Poison zones within range.
    const rangePixels = this.config.rangeTiles * TILE_SIZE;
    const rangeSq = rangePixels * rangePixels;
    const allZones = this.deps.zones.getAll();

    // Collect zone IDs to remove after iteration (avoid mutating during loop).
    const zonesToRemove: string[] = [];

    for (const zone of allZones) {
      if (!POISON_ZONE_TYPES.has(zone.config.type)) continue;

      const dx = zone.config.position.x - playerPos.x;
      const dy = zone.config.position.y - playerPos.y;
      if (dx * dx + dy * dy > rangeSq) continue;

      // Guard against permanent zones (remainingMs = -1): consume but no heal.
      const remainingSeconds = Math.max(0, zone.remainingMs) / 1000;
      healTotal += maxHp * this.config.healPerZoneSecondFraction * remainingSeconds;

      zonesToRemove.push(zone.config.id);
      zonesConsumed++;
    }

    for (const zoneId of zonesToRemove) {
      this.deps.zones.remove(zoneId);
    }

    // Cap heal at max HP — no overheal.
    const currentHp = this.deps.player.currentHp;
    const maxHeal = maxHp - currentHp;
    healTotal = Math.min(healTotal, Math.max(0, maxHeal));

    if (healTotal > 0) {
      this.deps.player.heal(healTotal);
    }

    // Record results for assertions / telemetry.
    this._lastHealTotal = healTotal;
    this._lastEnemiesConsumed = enemiesConsumed;
    this._lastZonesConsumed = zonesConsumed;
    this._activationsCount++;

    // Enter cooldown.
    this._state = 'cooldown';
    this._cooldownRemaining = this.config.cooldownMs;
    this._channelRemainingMs = 0;
    this._channelStartPosition = null;
  }
}
